import { ChevronsDown, ChevronsUp, X } from 'lucide-react';
import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { trapTab } from '../lib/focusTrap';
import { strings } from '../strings';
import { ATTR, useDock } from './context';
import { titleId } from './ids';
import { TabCount, WindowBody } from './WindowParts';

const s = strings.shell.phone;

/** Drag distance (px) that switches a snap point. */
const SNAP_DRAG = 40;

/**
 * A tool window as a bottom sheet below 768 px (spec 6.8.9): modal, with snap points at 50 % and 92 % of the
 * viewport height, a drag handle, Expand/Collapse and Close; Esc, Close and the scrim close it.
 */
export function BottomSheet({ id }: { id: string }) {
  const dock = useDock();
  const [expanded, setExpanded] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const definition = dock.definitions.find((d) => d.id === id);
  const title = definition?.title ?? id;
  const Icon = definition?.icon;

  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const startY = event.clientY;
    let delta = 0;
    const onMove = (move: PointerEvent) => {
      delta = move.clientY - startY;
      setDragOffset(Math.max(-window.innerHeight * 0.42, delta));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      setDragOffset(0);
      if (delta < -SNAP_DRAG) setExpanded(true);
      else if (delta > SNAP_DRAG) {
        if (expanded) setExpanded(false);
        else dock.hide(id);
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  return (
    <>
      <div aria-hidden className="sheet-scrim" onClick={() => dock.hide(id)} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId(id)}
        tabIndex={-1}
        data-placement="sheet"
        {...{ [ATTR.frame]: id }}
        className={`sheet${expanded ? ' is-expanded' : ''}`}
        style={
          dragOffset
            ? {
                transform: `translateY(${Math.max(0, dragOffset)}px)`,
                height: dragOffset < 0 ? `calc(${expanded ? 92 : 50}dvh - ${dragOffset}px)` : undefined,
              }
            : undefined
        }
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !event.defaultPrevented) {
            event.preventDefault();
            event.stopPropagation();
            dock.hide(id);
          } else if (ref.current) {
            trapTab(event, ref.current);
          }
        }}
      >
        <div className="sheet-handle" onPointerDown={startDrag} title={s.dragHandle}>
          <span aria-hidden />
        </div>
        <div className="sheet-head">
          <h2 id={titleId(id)} className="tw-title text-[15px]">
            {Icon && <Icon aria-hidden className="size-4 shrink-0" />}
            <span className="truncate">{title}</span>
            <TabCount badge={dock.badges.get(id)} />
          </h2>
          <button
            type="button"
            className="phone-icon-button"
            aria-label={expanded ? s.collapse(title) : s.expand(title)}
            title={expanded ? s.collapse(title) : s.expand(title)}
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? (
              <ChevronsDown aria-hidden className="size-5" />
            ) : (
              <ChevronsUp aria-hidden className="size-5" />
            )}
          </button>
          <button
            type="button"
            className="phone-icon-button"
            aria-label={s.close(title)}
            title={s.close(title)}
            onClick={() => dock.hide(id)}
          >
            <X aria-hidden className="size-5" />
          </button>
        </div>
        <WindowBody id={id} />
      </div>
    </>
  );
}

/** Below 768 px, the buttons that open the page's tool windows as sheets (spec 6.8.9). */
export function SheetLaunchers() {
  const dock = useDock();
  const windows = dock.definitions.filter((d) => dock.available.has(d.id));
  if (windows.length === 0) return null;
  return (
    <div role="toolbar" aria-label={s.launchers} className="sheet-launchers">
      {windows.map((definition) => {
        const Icon = definition.icon;
        return (
          <button
            key={definition.id}
            type="button"
            className="btn sheet-launcher"
            aria-haspopup="dialog"
            data-window-button={definition.id}
            onClick={() => dock.show(definition.id)}
          >
            <Icon aria-hidden className="size-4" />
            {definition.title}
            <TabCount badge={dock.badges.get(definition.id)} />
          </button>
        );
      })}
    </div>
  );
}
